import SegmentService, { EventGroup, EventTypeInteraction } from "module/analytics/service/SegmentService"
import { EventName } from "module/analytics/types"
import { useNotification } from "module/notification/hook/useNotification"
import { isUserDeniedSignature } from "util/is"

import { ContractReadErrorParams, Position, PositionSide, ReducePositionInvalidError } from "@perp/sdk-curie"
import Big from "big.js"
import { ActiveLimitOrdersContainer } from "container/service/LimitOrderServiceContainer/useActiveLimitOrder"
import { useCallback, useEffect, useMemo, useState } from "react"
import { PositionOpen } from "sdk-react/clearingHouse/usePositionOpen"
import { usePerpConnectedCallbackAsync } from "sdk-react/perpetualProtocol/usePerpConnectedCallbackAsync"
import { usePerpConnectedEffectAsync } from "sdk-react/perpetualProtocol/usePerpConnectedEffectAsync"
import { Handlers } from "sdk-react/tools/useHandlers"
import { TxLoading } from "sdk-react/tools/useTxLoading"
import { createContainer } from "unstated-next"

import { strToBig } from "../../util/format"
import { MIN_ORDER_VALUE_IN_USD } from "./constants"
import { OrderType } from "./types"

interface CreateLimitOrderInitialState {
    tickerSymbol: string
    baseAssetSymbol: string
    sideDefault?: PositionSide
    existingPosition?: Position
}

const DEFAULT_SIDE = PositionSide.LONG
const DEFAULT_BUYING_POWER_RATIO = 0
const DEFAULT_REDUCE_ONLY_POSITION_SIZE_RATIO = 0
const WEEK_IN_SECONDS = 7 * 24 * 60 * 60

export const CreateLimitOrder = createContainer(useCreateLimitOrder)

function useCreateLimitOrder({
    tickerSymbol,
    baseAssetSymbol,
    sideDefault = DEFAULT_SIDE,
    existingPosition,
}: CreateLimitOrderInitialState) {
    const { createLimitOrder } = ActiveLimitOrdersContainer.useContainer()
    const { buyingPower } = PositionOpen.useContainer()

    /* ========== STATUS HANDLERS ========== */
    const { getHandlers } = Handlers.useContainer()
    const { notifySuccess, notifyError } = useNotification()
    const { onError } = getHandlers()
    const { isTxLoading, setTxLoading } = TxLoading.useContainer()

    /* ========== INPUT ========== */
    const [limitPriceString, setLimitPriceString] = useState("")
    const [side, setSide] = useState<PositionSide>(sideDefault)
    const [quoteTokenAmountString, setQuoteTokenAmountString] = useState("")
    const [baseTokenAmountString, setBaseTokenAmountString] = useState("")
    const [buyingPowerRatio, setBuyingPowerRatio] = useState(DEFAULT_BUYING_POWER_RATIO)
    const [isReduceOnly, setIsReduceOnly] = useState(false)
    const [reduceOnlyPositionSizeRatio, setReduceOnlyPositionSizeRatio] = useState(
        DEFAULT_REDUCE_ONLY_POSITION_SIZE_RATIO,
    )

    const [deadline, setDeadline] = useState(WEEK_IN_SECONDS)

    /* ========== PERIPHERAL ========== */
    // const [buyingPower, setBuyingPower] = useState<Big>()
    const [entryPrice, setEntryPrice] = useState<Big>()
    const [priceImpact, setPriceImpact] = useState<Big>()
    const [transactionFee, setTransactionFee] = useState<Big>()

    /* ========== STATUS ========== */
    const [errorQuoteAmount, setErrorQuoteAmount] = useState<Error | undefined>()
    const [errorReduceOnlyRatio, setErrorReduceOnlyRatio] = useState<Error | undefined>()
    const [isBaseAmountFocus, setIsBaseAmountFocus] = useState<boolean>()
    const hasOppositePosition = useMemo(() => {
        return existingPosition ? existingPosition.side !== side : false
    }, [existingPosition, side])

    const resetInputsAndInformation = useCallback(() => {
        setLimitPriceString("")
        setQuoteTokenAmountString("")
        setBaseTokenAmountString("")
        setIsBaseAmountFocus(undefined)
        setErrorQuoteAmount(undefined)
        setErrorReduceOnlyRatio(undefined)
        setEntryPrice(undefined)
        setPriceImpact(undefined)
        setTransactionFee(undefined)
        setBuyingPowerRatio(DEFAULT_BUYING_POWER_RATIO)
        setReduceOnlyPositionSizeRatio(DEFAULT_REDUCE_ONLY_POSITION_SIZE_RATIO)
    }, [])

    const quoteTokenAmount = useMemo(() => strToBig(quoteTokenAmountString), [quoteTokenAmountString])
    const baseTokenAmount = useMemo(() => strToBig(baseTokenAmountString), [baseTokenAmountString])
    const limitPrice = useMemo(() => strToBig(limitPriceString), [limitPriceString])

    // NOTE: Clear state when market change.
    useEffect(() => {
        resetInputsAndInformation()
    }, [resetInputsAndInformation, tickerSymbol])

    // NOTE: Manage quoteTokenAmount error.
    useEffect(() => {
        if (quoteTokenAmount?.lt(MIN_ORDER_VALUE_IN_USD)) {
            setErrorQuoteAmount(new Error(`Order value must be greater than $${MIN_ORDER_VALUE_IN_USD}`))
        } else {
            setErrorQuoteAmount(undefined)
        }
    }, [quoteTokenAmount])

    useEffect(() => {
        if (!quoteTokenAmount || !baseTokenAmount) {
            return
        }
        // TODO: will use clearingHouseConfig.marketExchangeFeeRatios
        setTransactionFee(quoteTokenAmount.mul(0.1 / 100))
    }, [quoteTokenAmount, baseTokenAmount])

    // NOTE: update buyingPowerRatio
    useEffect(() => {
        if (!buyingPower || !quoteTokenAmount) {
            return
        }
        setBuyingPowerRatio(buyingPower.lte(0) ? 0 : quoteTokenAmount.div(buyingPower).toNumber())
    }, [buyingPower, quoteTokenAmount])

    // NOTE: manage reduceOnlyPositionSizeRatio when position or baseTokenAmount changed
    useEffect(() => {
        if (!existingPosition || !baseTokenAmount || !isReduceOnly) {
            setErrorReduceOnlyRatio(undefined)
            return
        }
        const existingPositionSize = existingPosition.sizeAbs
        const reduceOnlyPositionSizeRatio = existingPositionSize.lte(0)
            ? 0
            : baseTokenAmount.div(existingPositionSize).mul(100).toNumber()
        setReduceOnlyPositionSizeRatio(reduceOnlyPositionSizeRatio)
        if (reduceOnlyPositionSizeRatio > 100) {
            setErrorReduceOnlyRatio(new ReducePositionInvalidError({} as ContractReadErrorParams<any>))
        } else {
            setErrorReduceOnlyRatio(undefined)
        }
    }, [existingPosition, baseTokenAmount, isReduceOnly])

    const updateAmountBase = useCallback(
        (amountInput: string) => {
            if (amountInput === "") {
                resetInputsAndInformation()
                return
            }
            if (!limitPrice) {
                return
            }
            const outputAmount = new Big(amountInput).mul(limitPrice)
            setBaseTokenAmountString(amountInput)
            setQuoteTokenAmountString(outputAmount.toString())
            setIsBaseAmountFocus(true)
        },
        [resetInputsAndInformation, limitPrice],
    )

    const updateAmountQuote = useCallback(
        (amountInput: string) => {
            if (amountInput === "") {
                resetInputsAndInformation()
                return
            }
            if (!limitPrice) {
                return
            }
            const outputAmount = new Big(amountInput).div(limitPrice)
            setQuoteTokenAmountString(amountInput)
            setBaseTokenAmountString(outputAmount.toString())
            setIsBaseAmountFocus(false)
        },
        [resetInputsAndInformation, limitPrice],
    )

    const updateBuyingPowerRatio = useCallback(
        (ratio: number) => {
            if (!buyingPower) {
                return
            }

            const amountQuote = buyingPower.mul(ratio)
            updateAmountQuote(amountQuote.toString())
            setBuyingPowerRatio(ratio)
        },
        [buyingPower, updateAmountQuote],
    )

    const updatePositionSizeForReduceOnly = useCallback(
        (ratio: number) => {
            if (!existingPosition) {
                return
            }

            const amountBase = existingPosition.sizeAbs.mul(ratio).div(100)
            updateAmountBase(amountBase.toString())
            setReduceOnlyPositionSizeRatio(ratio)
        },
        [updateAmountBase, existingPosition],
    )

    const toggleSide = useCallback(() => {
        setIsReduceOnly(false)
        setSide(prev => (prev === PositionSide.LONG ? PositionSide.SHORT : PositionSide.LONG))
    }, [])

    const isInputReady = useMemo(() => {
        return !!side && quoteTokenAmount?.gt(0) && baseTokenAmount?.gt(0) && limitPrice?.gt(0)
    }, [baseTokenAmount, quoteTokenAmount, side, limitPrice])

    const updateLimitPrice = useCallback(
        (value: string) => {
            setLimitPriceString(value)

            if (value === "") {
                resetInputsAndInformation()
                return
            }

            if (!!side && quoteTokenAmount?.gt(0) && baseTokenAmount?.gt(0)) {
                const limitPrice = new Big(value)
                if (isBaseAmountFocus) {
                    setQuoteTokenAmountString(limitPrice.mul(baseTokenAmount).toString())
                } else {
                    setBaseTokenAmountString(quoteTokenAmount.div(limitPrice).toString())
                }
            }
        },
        [isBaseAmountFocus, baseTokenAmount, quoteTokenAmount, side, resetInputsAndInformation],
    )

    const [triggerPriceString, updateTriggerPrice] = useState("")
    const triggerPrice = useMemo(() => strToBig(triggerPriceString), [triggerPriceString])

    const [isStopOrderSupported, setIsStopOrderSupported] = useState(false)
    usePerpConnectedEffectAsync(
        async ({ limitOrderBook }) => {
            // TODO: [429 issue] check if we can reduce contract calls
            const roundId = await limitOrderBook.getPriceFeedLatestRound({ tickerSymbol })
            setIsStopOrderSupported(!!roundId)
        },
        [tickerSymbol],
    )

    const handleSubmit = usePerpConnectedCallbackAsync<OrderType>(
        async ({ limitOrderBook }, orderType) => {
            if (!side || !quoteTokenAmount || !baseTokenAmount || !limitPrice) {
                return
            }
            const deadlineTimestamp = Math.floor(Date.now() / 1000) + deadline
            setTxLoading(true)
            try {
                const roundIdWhenCreated = await limitOrderBook.getPriceFeedLatestRound({ tickerSymbol })
                const payload = {
                    tickerSymbol,
                    orderType,
                    side,
                    quoteAmount: quoteTokenAmount,
                    baseAmount: baseTokenAmount,
                    triggerPrice,
                    limitPriceForDisplay: limitPrice,
                    isReduceOnly,
                    deadline: deadlineTimestamp,
                    roundIdWhenCreated,
                }

                SegmentService.track(EventGroup.INTERACTION, {
                    eventType: EventTypeInteraction.OTHERS,
                    eventName: EventName.LIMIT_ORDER_SUBMIT,
                    payload: {
                        ...payload,
                        quoteAmount: quoteTokenAmount.toNumber(),
                        baseAmount: baseTokenAmount.toNumber(),
                        limitPriceForDisplay: limitPrice.toNumber(),
                        triggerPrice: triggerPrice ? triggerPrice.toNumber() : "",
                        roundIdWhenCreated: roundIdWhenCreated || "",
                    },
                })

                await createLimitOrder(payload)

                notifySuccess({
                    title: "Limit Order Sent Successfully",
                    description: "Orders should be updated shortly.",
                    duration: 10 * 1000,
                })
                setTxLoading(false)
                resetInputsAndInformation()
            } catch (error) {
                if (isUserDeniedSignature(error)) {
                    notifyError({ title: "Signing Limit Order Rejected" })
                } else {
                    notifyError({ title: "Something went wrong." })
                    onError(error as Error)
                }
                setTxLoading(false)
            }
        },
        [
            side,
            quoteTokenAmount,
            baseTokenAmount,
            deadline,
            setTxLoading,
            tickerSymbol,
            createLimitOrder,
            triggerPrice,
            isReduceOnly,
            notifySuccess,
            resetInputsAndInformation,
            notifyError,
            onError,
        ],
    )

    return {
        // NOTE: Inputs
        limitPrice,
        limitPriceString,
        triggerPrice,
        triggerPriceString,
        isReduceOnly,
        deadline,

        side,
        baseTokenAmountString,
        quoteTokenAmountString,
        baseTokenAmount,
        quoteTokenAmount,
        buyingPowerRatio,
        reduceOnlyPositionSizeRatio,
        hasOppositePosition,

        // NOTE: Handlers
        toggleSide,
        setSide,
        setIsReduceOnly,
        updateAmountBase,
        updateAmountQuote,
        updateBuyingPowerRatio,
        updateLimitPrice,
        updateTriggerPrice,
        updatePositionSizeForReduceOnly,
        handleSubmit,
        setDeadline,

        // NOTE: Peripheral
        baseAssetSymbol,
        buyingPower,

        // NOTE: Status
        isStopOrderSupported,
        isInputReady,
        isTxLoading,
        error: errorQuoteAmount || errorReduceOnlyRatio,

        // NOTE: positionDraft v2
        entryPrice,
        priceImpact,
        transactionFee,
    }
}
